/* Massform Compare page (2026-10-21; sections/mf-compare-page.liquid): up to three panels side by side, and
   their absorption curves on one chart. The page hands over the store's products with their register
   figures, curves and sizes; this file only lists which products make one model, and the four reference
   materials. */

// one entry per model; finishes share the model's figures, curves and size
const MODELS = [
  ['surge-2x2', 'Surge Pro 2′ × 2′', [['surge-2x2-sandstone', 'Sandstone'], ['surge-2x2-silver-nimbus', 'Silver Nimbus']]],
  ['surge-2x4', 'Surge Pro 2′ × 4′', [['surge-2x4-sandstone', 'Sandstone'], ['surge-2x4-silver-nimbus', 'Silver Nimbus']]],
  ['rhythm-2x2', 'Rhythm Pro 2′ × 2′', [['rhythm-2x2-sandstone', 'Sandstone'], ['rhythm-2x2-silver-nimbus', 'Silver Nimbus']]],
  ['rhythm-2x4', 'Rhythm Pro 2′ × 4′', [['rhythm-2x4-sandstone', 'Sandstone'], ['rhythm-2x4-silver-nimbus', 'Silver Nimbus']]],
  ['cymbals-2x2', 'Cymbals Pro 2′ × 2′', [['cymbals-pro-2x2', '']]],
  ['eclipses-2x2', 'Eclipses Pro 2′ × 2′', [['eclipses-pro-2x2', '']]],
  ['wave', 'Wave', [['wave-sandstone', 'Sandstone'], ['wave-silver-nimbus', 'Silver Nimbus']]],
  ['thresholds', 'Thresholds', [['m-c-escher-sandstone', 'Sandstone'], ['m-c-escher-moon-shadow', 'Moon Shadow']]],
  ['grand-canyon', 'Grand Canyon', [['grand-canyon', '']]],
  ['glen-canyon', 'Glen Canyon', [['glen-canyon', '']]],
  ['sonic-flow', 'Sonic Flow', [['curved-architecture', '']]],
  ['modular-systems', 'Modular Systems', [['modular-architecture', '']]],
  ['color-field', 'Color Field', [['rothkotone-set', '']]],
  ['moogdulation', 'Moogdulation', [['moogdulation-set', '']]],
];
const DEFAULT = ['surge-2x2', 'rhythm-2x2', 'wave'];
// the product pages' curves: 18 third-octave bands
const BANDS = [100, 125, 160, 200, 250, 315, 400, 500, 630, 800, 1000, 1250, 1600, 2000, 2500, 3150, 4000, 5000];
// octave bands, 125 Hz to 4 kHz
const OCTAVES = [125, 250, 500, 1000, 2000, 4000];
// The two reference panels (2026-10-21, Corey: "a 25mm foam, those cheap foams ... and then the fiberboards, as
// in GIK's impression 2 inch ... in different colors"): each its own colour, dotted, so neither reads as a Massform
// series. Octave bands, each the mean of the report's three third-octave bands, like the Massform curves; sources in
// acoustic-evidence.md, amendment 2026-10-25 (two references).
const REFS = [
  ['Acoustic foam, 25 mm', [0.10, 0.13, 0.31, 0.68, 0.93, 1.02], '#c2477a'],
  ['Fibre-board acoustic panel, 50 mm', [0.25, 0.75, 1.07, 1.03, 0.99, 1.00], '#1f86d6'],
];
const COLOURS = ['#15191a', '#138a52', '#b56b4c'];
// The chart compares like for like (2026-10-21, Corey: "i don't think the common panels is that much better than
// ours ... especially in the lower ends"): every curve in octave bands, as the boards are, each octave the mean of
// its three third-octave bands (the EN ISO 11654 method, acoustic-evidence.md). Families measured in Massform's
// office room are drawn from 250 Hz: below the room's Schroeder frequency, about 200 Hz, its resonances scatter the
// result around zero (the register: "the panels add nothing measurable there"), so those bands are not data.
const THIRDS = [[100, 125, 160], [200, 250, 315], [400, 500, 630], [800, 1000, 1250], [1600, 2000, 2500], [3150, 4000, 5000]];
const IN_ROOM = new Set(['rhythm', 'cymbals', 'colorfield', 'moog']);
const ROOM_FLOOR = 250;
function octaves(curve, inRoom) {
  return THIRDS.map((group) => {
    const vals = group.filter((f) => !inRoom || f >= ROOM_FLOOR).map((f) => curve[BANDS.indexOf(f)]).filter(Number.isFinite);
    return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
  });
}
const MAX = 1.5;

const root = document.querySelector('.mf-cmp');
const data = JSON.parse(root.querySelector('[data-mf-cmp]').textContent);
const P = data.products;
const models = MODELS.filter(([, , fins]) => fins.some(([h]) => P[h]));
const byKey = Object.fromEntries(models.map((m) => [m[0], m]));

const asCurve = (v) => {
  try {
    const a = typeof v === 'string' ? JSON.parse(v) : v;
    if (!Array.isArray(a)) return null;
    const n = a.map(Number);
    return n.length && n.every(Number.isFinite) ? n : null;
  } catch (e) {
    return null;
  }
};
const el = (tag, cls, text) => {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
};

let picks = (new URLSearchParams(location.search).get('m') || '').split(',').filter((k) => byKey[k]).slice(0, 3);
if (!picks.length) picks = DEFAULT.filter((k) => byKey[k]);
// opened from a product page with one model: the defaults fill the other two
if (picks.length === 1) picks = [picks[0], ...DEFAULT.filter((k) => byKey[k] && k !== picks[0])].slice(0, 3);
while (picks.length < 3) picks.push('');
// -1: not chosen yet, so the first finish in stock shows
const finish = [-1, -1, -1];

// the product shown for a pick: its chosen finish, among the finishes the store sells
function pick(i) {
  const m = byKey[picks[i]];
  if (!m) return null;
  const fins = m[2].filter(([h]) => P[h]);
  if (finish[i] < 0) finish[i] = Math.max(0, fins.findIndex(([fh]) => P[fh].price));
  const [h, finishName] = fins[Math.min(finish[i], fins.length - 1)];
  const nrc = P[h].nrc || [];
  // finishes share curves; take them from whichever finish carries them
  let plain = null;
  let withb = null;
  for (const [fh] of fins) {
    plain = plain || asCurve(P[fh].plain);
    withb = withb || asCurve(P[fh].withb);
  }
  return { m, fins, h, finishName, p: P[h], nrc, plain, withb };
}

function pickers() {
  const box = root.querySelector('[data-pickers]');
  box.replaceChildren(...picks.map((k, i) => {
    const label = el('label', 'mf-cmp__pick');
    const sel = el('select');
    if (i > 0) sel.append(new Option('None', ''));
    for (const [key, name] of models) sel.append(new Option(name, key));
    sel.value = k;
    sel.setAttribute('aria-label', `Panel ${i + 1}`);
    sel.addEventListener('change', () => {
      picks[i] = sel.value;
      finish[i] = -1;
      render();
    });
    label.append(el('span', 'mf-cmp__pick-n', `Panel ${i + 1}`), sel);
    return label;
  }));
}

function bars(nrc) {
  const box = el('div', 'mf-cmp__bars');
  if (!nrc.length || !nrc[1]) {
    box.append(el('span', 'mf-cmp__none', 'No tested figure yet'));
    return box;
  }
  const one = (label, value, tag) => {
    const b = el('div', 'mf-cmp__bar' + (tag === 'calculated' || tag === 'estimated' ? ' is-derived' : ''));
    const v = el('span', 'mf-cmp__val', value + ' ');
    v.append(el('small', null, tag));
    const track = el('span', 'mf-cmp__track');
    const fill = el('i');
    fill.style.width = `${Math.min(100, (Number(value) / MAX) * 100)}%`;
    track.append(fill);
    b.append(el('span', 'mf-cmp__lab', label), v, track);
    return b;
  };
  if (nrc[3]) {
    box.append(one('Without Basstrap', nrc[1], nrc[2]), one('With Basstrap', nrc[3], nrc[4]));
  } else {
    box.append(one('On its own', nrc[1], nrc[2]));
  }
  return box;
}

function table() {
  const cols = picks.map((k, i) => (k ? i : -1)).filter((i) => i >= 0);
  const t = root.querySelector('[data-table]');
  t.style.setProperty('--cols', cols.length);
  const row = (label, cells, cls = '') => {
    const r = el('div', 'mf-cmp__row' + (cls ? ' ' + cls : ''));
    if (label) r.append(el('div', 'mf-cmp__label', label));
    r.append(...cells);
    return r;
  };
  const text = (s) => el('div', 'mf-cmp__cell', s || '');
  const head = (i) => {
    const x = pick(i);
    const c = el('div', 'mf-cmp__cell mf-cmp__headcell');
    c.style.setProperty('--colour', COLOURS[i]);
    if (x.p.img) {
      const img = el('img', 'mf-cmp__img');
      img.src = x.p.img;
      img.alt = x.p.alt || x.m[1];
      img.loading = 'lazy';
      c.append(img);
    }
    c.append(el('span', 'mf-cmp__name', x.m[1]));
    if (x.fins.length > 1) {
      const chips = el('div', 'mf-cmp__finishes');
      x.fins.forEach(([, name], fi) => {
        const b = el('button', 'mf-cmp__finish', name);
        b.type = 'button';
        b.setAttribute('aria-pressed', String(fi === Math.min(finish[i], x.fins.length - 1)));
        b.addEventListener('click', () => {
          finish[i] = fi;
          render();
        });
        chips.append(b);
      });
      c.append(chips);
    }
    c.append(el('span', 'mf-cmp__price', x.p.price ? `From ${x.p.price}${x.p.unit ? ' · ' + x.p.unit : ''}` : 'Sold out at the moment'));
    const go = el('a', 'mf-btn mf-cmp__go', 'View and buy');
    go.href = x.p.url;
    c.append(go);
    return c;
  };
  const hear = (i) => {
    const x = pick(i);
    const c = el('div', 'mf-cmp__cell');
    if (x.p.hear) {
      const a = el('a', 'mf-link', 'Hear it in a room like yours →');
      a.href = data.hearUrl + encodeURIComponent(x.h);
      c.append(a);
    }
    return c;
  };
  t.replaceChildren(
    row(null, cols.map(head), 'mf-cmp__row--head'),
    row('How much sound it absorbs (NRC)', cols.map((i) => {
      const c = el('div', 'mf-cmp__cell');
      c.append(bars(pick(i).nrc));
      return c;
    })),
    row('Size', cols.map((i) => text(pick(i).p.spec[0]))),
    row('Thickness', cols.map((i) => text(pick(i).p.spec[1] || 'Not listed yet'))),
    row('Made of', cols.map((i) => text(pick(i).p.spec[2]))),
    row('The shape', cols.map((i) => text(pick(i).nrc[6] || ''))),
    row('How we know', cols.map((i) => text(pick(i).nrc[5] || 'Not tested yet.')), 'mf-cmp__row--how'),
    row('Hear it', cols.map(hear)),
  );
}

function chart() {
  const W = 640;
  const H = 300;
  const L = 34;
  const R = 10;
  const T = 10;
  const B = 46;
  const showBass = root.querySelector('[data-bass]').checked;
  const showRefs = root.querySelector('[data-refs]').checked;
  // the scale fits the curves shown (Cymbals Pro runs above 1.5), in steps of 0.5
  const oct = (x, c) => (c ? octaves(c, IN_ROOM.has(x.p.fam)) : null);
  let top = 1.5;
  picks.forEach((k, i) => {
    if (!k) return;
    const x = pick(i);
    for (const c of [oct(x, x.plain), showBass ? oct(x, x.withb) : null, !x.nrc[3] ? oct(x, x.withb) : null]) {
      if (c) top = Math.max(top, Math.ceil(Math.max(...c.filter(Number.isFinite)) * 2) / 2);
    }
  });
  const fx = (f) => L + (Math.log(f / 100) / Math.log(5000 / 100)) * (W - L - R);
  const fy = (a) => T + (1 - Math.max(0, Math.min(a, top)) / top) * (H - T - B);
  // a curve with a gap (an octave not drawn) is drawn from its first value on
  const line = (freqs, vals, colour, dash, width) => {
    const pts = vals.map((v, k) => (Number.isFinite(v) ? `${fx(freqs[k]).toFixed(1)},${fy(v).toFixed(1)}` : null)).filter(Boolean);
    const dots = vals.map((v, k) => (Number.isFinite(v) ? `<circle cx="${fx(freqs[k]).toFixed(1)}" cy="${fy(v).toFixed(1)}" r="${width + 0.6}" fill="${colour}"/>` : '')).join('');
    return `<polyline fill="none" stroke="${colour}" stroke-width="${width}" stroke-linejoin="round" stroke-linecap="round"${dash ? ` stroke-dasharray="${dash}"` : ''} points="${pts.join(' ')}"/>${dash ? '' : dots}`;
  };
  let s = `<svg viewBox="0 0 ${W} ${H}" role="img" aria-label="Absorption from bass to treble for the panels picked">`;
  [[100, 250, 'Bass'], [250, 2000, 'Voices'], [2000, 5000, 'Treble']].forEach(([a, b, name], k) => {
    s += `<rect x="${fx(a)}" y="${T}" width="${fx(b) - fx(a)}" height="${H - T - B}" fill="${k % 2 ? '#eeebe3' : '#f7f5ef'}"/>`;
    s += `<text x="${(fx(a) + fx(b)) / 2}" y="${H - 8}" text-anchor="middle" class="mf-cmp__zone">${name}</text>`;
  });
  for (let a = 0.5; a <= top + 1e-9; a += 0.5) {
    s += `<line x1="${L}" x2="${W - R}" y1="${fy(a)}" y2="${fy(a)}" stroke="#ddd8cc"/>`;
    s += `<text x="${L - 6}" y="${fy(a) + 4}" text-anchor="end" class="mf-cmp__tick">${a.toFixed(1)}</text>`;
  }
  for (const f of OCTAVES) {
    s += `<text x="${fx(f)}" y="${H - B + 16}" text-anchor="middle" class="mf-cmp__tick">${f >= 1000 ? f / 1000 + 'k' : f} Hz</text>`;
  }
  if (showRefs) REFS.forEach(([, vals, colour]) => { s += line(OCTAVES, vals, colour, '2 5', 2.2); });
  const legend = [];
  let roomNote = false;
  // picks with the same curve (Surge Pro and Wave share one lab test) draw on top of each other: say so
  const drawn = [];
  picks.forEach((k, i) => {
    if (!k) return;
    const x = pick(i);
    if (!x.nrc.length || !x.nrc[1]) {
      legend.push([COLOURS[i], `${x.m[1]}: no tested curve yet`, '']);
      return;
    }
    const single = !x.nrc[3];
    const main = oct(x, single ? x.plain || x.withb : x.plain);
    const inRoom = IN_ROOM.has(x.p.fam);
    if (inRoom) roomNote = true;
    if (main) {
      s += line(OCTAVES, main, COLOURS[i], '', 2.6);
      const sig = main.map((v) => (Number.isFinite(v) ? v.toFixed(3) : '-')).join(',');
      const twin = drawn.find((d) => d.sig === sig);
      drawn.push({ sig, name: x.m[1] });
      legend.push([COLOURS[i], `${x.m[1]}${single ? '' : ', without Basstrap'} (${x.nrc[2]}${inRoom ? ', from 250 Hz' : ''})${twin ? `: the same test as ${twin.name}, so the lines lie on top of each other` : ''}`, '']);
    }
    const withb = oct(x, x.withb);
    if (!single && showBass && withb) {
      s += line(OCTAVES, withb, COLOURS[i], '7 5', 2.2);
      legend.push([COLOURS[i], `${x.m[1]}, with Basstrap (${x.nrc[4]}${inRoom ? ', from 250 Hz' : ''})`, 'dash']);
    }
  });
  if (showRefs) REFS.forEach(([name, , colour]) => legend.push([colour, `${name} (maker’s lab test, brand not named)`, 'dot']));
  if (roomNote) legend.push(['transparent', 'From 250 Hz: curves from Massform’s office room, where below about 200 Hz the room’s own resonances swamp the panel.', 'note']);
  // small print for the two references (2026-10-25, Corey: "a small print ... in the bottom ... so people would know")
  if (showRefs) legend.push(['transparent', 'Dotted lines: a 25 mm wedge foam and a 2-inch fabric-wrapped fibre panel (56 mm as tested), one common product of each type, brand not named, from its maker’s published laboratory test: Riverbank Acoustical Laboratories (ASTM C423, 1993) and the University of Salford (ISO 354, 2014). Both laid flat with no air gap; not measured by Massform.', 'fine']);
  s += '</svg>';
  // the boards are octave values too, so every line on the chart is drawn the same way
  root.querySelector('[data-chart]').innerHTML = s;
  root.querySelector('[data-legend]').replaceChildren(...legend.map(([colour, text, style]) => {
    const item = el('span', 'mf-cmp__key');
    const sw = el('i', style ? `is-${style}` : '');
    sw.style.setProperty('--colour', colour);
    item.append(sw, document.createTextNode(text));
    return item;
  }));
}

function render() {
  pickers();
  table();
  chart();
  const shown = picks.filter(Boolean).join(',');
  const url = new URL(location.href);
  url.searchParams.set('m', shown);
  history.replaceState(null, '', url);
}

root.querySelector('[data-bass]').addEventListener('change', chart);
root.querySelector('[data-refs]').addEventListener('change', chart);
render();
